/**
 * Centralized Default Program Route Resolver
 * 
 * This is the SINGLE SOURCE OF TRUTH for all post-login and program-entry redirects.
 * Uses configuration-driven routing — role → dashboard mapping comes from
 * `MENU_KEY_TO_ROUTE` and backend `defaultLandingRoute`.
 * 
 * GOVERNANCE RULES (STRICT):
 *   - SuperAdmin is handled outside this resolver
 *   - Hospital roles → /hospital-dashboard
 *   - HCP roles → /hcp-dashboard
 *   - Patient roles → /patient-dashboard
 *   - Operational/Pharma roles → /workspace
 *   - Configuration roles (Program Admin) → /workspace (forms/workflow accessible from nav)
 */

import { appRoutes, programRoutes } from '@/modules/shared/constants/routes';
import { MENU_KEYS } from '../services/programRoleMenuPermissionService';

/**
 * Menu key to route mapping.
 * This defines the actual navigation paths.
 * All routes are program-scoped (require programId).
 */
const MENU_KEY_TO_ROUTE: Record<string, (programId: string) => string> = {
  [MENU_KEYS.PROGRAM_DASHBOARD]: (programId) => programRoutes.dashboard(programId),
  [MENU_KEYS.OPERATIONAL_DASHBOARD]: (programId) => programRoutes.operationalDashboard(programId),
  [MENU_KEYS.WORKSPACE]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.PHARMA_WORKSPACE]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.HOSPITAL_WORKSPACE]: (programId) => programRoutes.hospitalDashboard(programId),
  [MENU_KEYS.HCP_WORKSPACE]: (programId) => programRoutes.hcpDashboard(programId),
  [MENU_KEYS.PATIENT_WORKSPACE]: (programId) => programRoutes.patientDashboard(programId),
  [MENU_KEYS.PATIENTS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.HOSPITALS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.HCPS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.ASSISTANCE_CASES]: (programId) => programRoutes.workspaceCases(programId),
  [MENU_KEYS.ASSISTANCE_CLAIMS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.REPORTS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.AUDIT_REPORT]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.NOTIFICATIONS_CONFIG]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.AUTOMATION_RULES]: (programId) => programRoutes.automationRules(programId),
  [MENU_KEYS.AUTOMATION_RULE_REVIEW]: (programId) => programRoutes.automationRuleReviewQueue(programId),
  [MENU_KEYS.AUTOMATION_RULE_HISTORY]: (programId) => programRoutes.automationRules(programId),
  [MENU_KEYS.CALL_LOGS]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.MANAGEMENT_DASHBOARD]: (programId) => programRoutes.dashboard(programId),
  [MENU_KEYS.PROGRAM_SELECTION]: (_programId) => appRoutes.programSelection(),
  [MENU_KEYS.FORMS_CONFIG]: (programId) => programRoutes.workspace(programId),
  [MENU_KEYS.WORKFLOW_CONFIG]: (programId) => programRoutes.workspace(programId),
};

interface ResolveRouteOptions {
  /** If true, this is a configuration role (Program Admin) */
  isConfigurationRole?: boolean;
  /** Role name for governance detection */
  roleName?: string;
}

/**
 * Resolves the default program route based on user's accessible menu keys.
 * 
 * CONFIGURATION-DRIVEN ALGORITHM:
 * 1. If role has a scoped workspace key (HOSPITAL_WORKSPACE, HCP_WORKSPACE,
 *    PATIENT_WORKSPACE), route to the matching dashboard
 * 2. If role is configuration-only (forms/workflow but no workspace), route to workspace
 * 3. Otherwise route to workspace (operational default)
 * 
 * @param accessibleMenuKeys - Array of menu keys the user can access (from API)
 * @param programId - The program ID to redirect to
 * @param options - Optional configuration for governance
 * @returns The default route to redirect to (program-scoped)
 */
export function resolveDefaultProgramRoute(
  accessibleMenuKeys: string[],
  programId: string,
  options?: ResolveRouteOptions
): string {
  // Validation
  if (!programId || programId.trim() === '') {
    throw new Error('resolveDefaultProgramRoute: programId is required and must not be empty');
  }

  const menuSet = new Set(accessibleMenuKeys || []);

  // Configuration roles (Program Admin) → operational-dashboard (unified landing).
  // The admin-dashboard remains accessible from sidebar navigation for governance tasks.
  if (options?.isConfigurationRole) {
    console.debug('resolveDefaultProgramRoute: Configuration role → /operational-dashboard', {
      roleName: options?.roleName,
    });
    return programRoutes.operationalDashboard(programId);
  }

  // Scoped dashboard routing — configuration-driven via MENU_KEY_TO_ROUTE
  const scopedKeys = [
    MENU_KEYS.HOSPITAL_WORKSPACE,
    MENU_KEYS.HCP_WORKSPACE,
    MENU_KEYS.PATIENT_WORKSPACE,
  ] as const;

  for (const key of scopedKeys) {
    if (menuSet.has(key)) {
      const routeFn = MENU_KEY_TO_ROUTE[key];
      if (routeFn) {
        const route = routeFn(programId);
        console.debug('resolveDefaultProgramRoute: Scoped dashboard route resolved', {
          menuKey: key,
          roleName: options?.roleName,
          resolvedRoute: route,
        });
        return route;
      }
    }
  }

  // Default: operational dashboard (CC/CCM/operational roles)
  console.debug('resolveDefaultProgramRoute: Default → /operational-dashboard', {
    accessibleMenuKeys,
    roleName: options?.roleName,
  });
  return programRoutes.operationalDashboard(programId);
}

/**
 * Retrieves the menu key to route mapping for a specific menu key.
 * Used by route guards and navigation logic.
 * 
 * @param menuKey - The menu key to look up
 * @param programId - The program ID to include in route
 * @returns The route for this menu key, or undefined if not found
 */
export function getRouteForMenuKey(menuKey: string, programId: string): string | undefined {
  const routeFn = MENU_KEY_TO_ROUTE[menuKey as keyof typeof MENU_KEY_TO_ROUTE];
  return routeFn ? routeFn(programId) : undefined;
}

/**
 * Checks if a given route is a legal/non-operational page.
 * Used for safety checks before redirect.
 * 
 * @param route - The route to check
 * @returns true if route is to a legal/non-operational page
 */
export function isNonOperationalRoute(route: string): boolean {
  return route.includes('/legal') || route.includes('/terms') || route.includes('/privacy');
}

/**
 * Validates that a route is program-scoped (includes programId).
 * Safety check to prevent unscoped navigation.
 * 
 * @param route - The route to validate
 * @returns true if route includes a program ID pattern
 */
export function isProgramScopedRoute(route: string): boolean {
  // Check for UUID pattern in route (programId format)
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  return uuidPattern.test(route) || /\/programs\/[a-zA-Z0-9\-]+\//.test(route);
}

/**
 * Extracts programId from a route if present.
 * 
 * @param route - The route to parse
 * @returns The programId if found, undefined otherwise
 */
export function extractProgramIdFromRoute(route: string): string | undefined {
  const match = route.match(/\/programs\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
  return match ? match[1] : undefined;
}
